import React from 'react'
import { combineClasses } from '~/util'
import { ExpandableHeaderImage } from '../expandable-header-image'
import styles from './FloorPlanImage.module.scss'

export interface FloorPlanImageProps
  extends React.ComponentProps<typeof ExpandableHeaderImage> {
  /**
   * Optional disclaimer text to display below the floor plan image.
   * This is typically used for artist's rendering disclaimers.
   */
  disclaimerText?: string | null
  /**
   * Optional class name for the disclaimer text element.
   */
  disclaimerClassName?: string
  /**
   * Optional class name for the container element.
   */
  containerClassName?: string
  /**
   * Optional content to render between the image and disclaimer text.
   * Useful for action links or other interactive elements.
   */
  children?: React.ReactNode
}

/**
 * `<FloorPlanImage>` displays a floor plan image with an optional disclaimer
 * text below it. This component wraps `ExpandableHeaderImage` and ensures
 * that floor plan disclaimer text is consistently displayed near the image
 * throughout the app.
 */
export function FloorPlanImage({
  disclaimerText,
  disclaimerClassName,
  containerClassName,
  className,
  children,
  onLoad,
  ...rest
}: FloorPlanImageProps) {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [textMaxWidth, setTextMaxWidth] = React.useState<number | undefined>()

  const calculateTextWidth = React.useCallback(() => {
    if (!containerRef.current) return

    // Find the actual img element within the container
    const img = containerRef.current.querySelector('img') as HTMLImageElement
    if (!img || !img.naturalWidth || !img.naturalHeight) return

    // Get the image element's rendered dimensions
    const imgElementWidth = img.clientWidth
    const imgElementHeight = img.clientHeight
    const imgAspectRatio = img.naturalWidth / img.naturalHeight
    const elementAspectRatio = imgElementWidth / imgElementHeight

    // With object-fit: contain, calculate actual visible image width
    let renderedWidth: number
    if (imgAspectRatio > elementAspectRatio) {
      // Image is wider than element (constrained by width)
      renderedWidth = imgElementWidth
    } else {
      // Image is taller than element (constrained by height)
      renderedWidth = imgElementHeight * imgAspectRatio
    }

    setTextMaxWidth(renderedWidth)
  }, [])

  const handleLoad = React.useCallback(
    (e?: React.SyntheticEvent<HTMLImageElement>) => {
      // Call the original onLoad if provided
      onLoad?.(e as React.SyntheticEvent<HTMLImageElement>)
      // Calculate text width after image loads
      calculateTextWidth()
    },
    [onLoad, calculateTextWidth]
  )

  return (
    <div
      ref={containerRef}
      className={combineClasses(styles.FloorPlanImage, containerClassName)}
    >
      <ExpandableHeaderImage
        className={className}
        onLoad={handleLoad}
        {...rest}
      />
      {children}
      {disclaimerText && (
        <p
          data-testid="FloorPlanDisclosure"
          className={combineClasses(styles.disclaimer, disclaimerClassName)}
          style={textMaxWidth ? { maxWidth: textMaxWidth } : undefined}
        >
          {disclaimerText}
        </p>
      )}
    </div>
  )
}
